import React from 'react';
import { Paper, Box, Typography, IconButton, Collapse, Divider, Button, Chip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import AppInfo from '../AppInfo';
import ToolbarControls from './ToolbarControls';
import PdfReportGenerator from './PdfReportGenerator';
import { useData } from '../DataProvider';
import { getPlatformLabel } from '../bucketLabels';

const DashboardHeader = ({ 
  appInfo,
  appInfoExpanded,
  onToggleAppInfo,
  readOnly = false,
  onReadOnlyToggle,
  onOpenViewSettings,
  fullScreenPanel = null,
  onExitFullScreen,
  viewConfig = {}
}) => {
  const inFullScreen = fullScreenPanel != null;
  // The run's platform (the metadata.json tag; no tag = Windows) — shown as a chip
  // next to the app name so a macOS run is recognisable at a glance.
  const { platform, metadata } = useData();
  const platformLabel = getPlatformLabel(platform);
  const platformTitle = metadata?.PlatformVersion || appInfo?.platformVersion || platformLabel;

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        width: '100%',
        flexShrink: 0,
        borderRadius: 0,
        borderBottom: 1,
        borderColor: 'divider'
      }}
    >
      <Box 
        sx={{ 
          p: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="h1" fontWeight="bold">
            {appInfo.appName || 'App Inspector'}
          </Typography>
          {platformLabel && (
            <Chip
              size="small"
              label={platformLabel}
              title={platformTitle}
              color={platform === 'macos' ? 'secondary' : 'default'}
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {inFullScreen && onExitFullScreen != null ? (
            <Button
              size="small"
              startIcon={<FullscreenExitIcon />}
              onClick={(e) => { e.stopPropagation(); onExitFullScreen(); }}
              variant="contained"
            >
              Exit fullscreen
            </Button>
          ) : (
          <>
          {!!viewConfig?.reportsSection && (
            <PdfReportGenerator appInfo={appInfo} viewConfig={viewConfig} />
          )}
          <ToolbarControls
            readOnly={readOnly}
            onReadOnlyToggle={onReadOnlyToggle}
            onOpenViewSettings={onOpenViewSettings}
          />
          
          <Divider orientation="vertical" flexItem />
          
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleAppInfo(); }}>
            {appInfoExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          </>
          )}
        </Box>
      </Box>
      
      {!inFullScreen && <Collapse in={appInfoExpanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <AppInfo 
            metadataUrl={appInfo.metadata} 
            inspectionName={appInfo.inspection} 
            appName={appInfo.appName} 
            version={appInfo.version} 
            notes={appInfo.notes} 
          />
        </Box>
      </Collapse>}
    </Paper>
  );
};

export default DashboardHeader;
